import {
    Warning as WarningIcon,
    Today,
    PriorityHigh,
    Inbox as InboxIcon,
} from "@mui/icons-material";
import { differenceInDays, formatDate, toLocalDay } from "./dateUtils";

export const PRIORITY_ORDER = { Highest: 4, High: 3, Medium: 2, Low: 1, Lowest: 0 };

export const URGENCY_ORDER = ["critical", "high", "thisWeek", "upcoming"];

export const URGENCY_META = {
    critical: {
        label: "Now",
        shortLabel: "Now",
        hint: "Overdue or marked rush",
        icon: WarningIcon,
        accent: "error",
    },
    high: {
        label: "High priority",
        shortLabel: "High priority",
        hint: "Highest and high priority work",
        icon: PriorityHigh,
        accent: "warn",
    },
    thisWeek: {
        label: "Due this week",
        shortLabel: "This week",
        hint: "Within the next 7 days",
        icon: Today,
        accent: "info",
    },
    upcoming: {
        label: "Upcoming",
        shortLabel: "Upcoming",
        hint: "Future work and routine items",
        icon: InboxIcon,
        accent: "neutral",
    },
};

export const isTask = (item) => item?.type === "Task";

export const isProject = (item) => item?.type === "Project";

export const isResponsible = (item) => item?.relation !== "assignee";

export const daysUntilDue = (dueDate) => {
    const due = formatDate(dueDate);
    if (!due) return null;
    return differenceInDays(due, toLocalDay());
};

export const dueTone = (dueDate) => {
    const diff = daysUntilDue(dueDate);
    if (diff == null) return "normal";
    if (diff < 0) return "late";
    if (diff === 0) return "today";
    if (diff <= 3) return "soon";
    return "normal";
};

export const dueShort = (dueDate) => {
    const diff = daysUntilDue(dueDate);
    if (diff == null) return null;
    if (diff < 0) return `${Math.abs(diff)}d late`;
    if (diff === 0) return "today";
    if (diff === 1) return "tomorrow";
    if (diff <= 7) return `${diff}d`;
    return null;
};

// Urgency is a property of tasks only. A project without tasks is waiting, not late.
export const urgencyOf = (item) => {
    if (!isTask(item)) return null;
    const days = daysUntilDue(item.dueDate);
    if ((days != null && days < 0) || item.rush) return "critical";
    if (item.priority === "Highest" || item.priority === "High") return "high";
    if (days != null && days <= 7) return "thisWeek";
    return "upcoming";
};

const dueTime = (item) => (item.dueDate ? new Date(formatDate(item.dueDate)).getTime() : Infinity);

export const compareAssignments = (a, b) => {
    const relation = (isResponsible(a) ? 0 : 1) - (isResponsible(b) ? 0 : 1);
    if (relation !== 0) return relation;
    if (!!a.rush !== !!b.rush) return a.rush ? -1 : 1;
    const priority = (PRIORITY_ORDER[b.priority] ?? 0) - (PRIORITY_ORDER[a.priority] ?? 0);
    if (priority !== 0) return priority;
    return dueTime(a) - dueTime(b);
};

export const sortAssignments = (items) => [...(items || [])].sort(compareAssignments);

export const sortTasksByUrgency = (items) =>
    [...(items || [])].sort((a, b) => {
        const relation = (isResponsible(a) ? 0 : 1) - (isResponsible(b) ? 0 : 1);
        if (relation !== 0) return relation;
        const urgency = URGENCY_ORDER.indexOf(urgencyOf(a)) - URGENCY_ORDER.indexOf(urgencyOf(b));
        if (urgency !== 0) return urgency;
        return compareAssignments(a, b);
    });

const byAccountThenName = (a, b) => {
    const account = (a.account?.name || "").localeCompare(b.account?.name || "");
    if (account !== 0) return account;
    return (a.name || "").localeCompare(b.name || "");
};

export const sortProjects = (items) =>
    [...(items || [])].sort((a, b) => {
        const relation = (isResponsible(a) ? 0 : 1) - (isResponsible(b) ? 0 : 1);
        if (relation !== 0) return relation;
        return byAccountThenName(a, b);
    });

export const bucketTasksByUrgency = (tasks) => {
    const buckets = { critical: [], high: [], thisWeek: [], upcoming: [] };
    for (const task of tasks || []) {
        const key = urgencyOf(task);
        if (key) buckets[key].push(task);
    }
    for (const key of URGENCY_ORDER) buckets[key] = sortAssignments(buckets[key]);
    return buckets;
};

export const splitByType = (items) => {
    const list = items || [];
    return {
        tasks: list.filter(isTask),
        projects: list.filter(isProject),
    };
};

export const splitByRelation = (items) => {
    const list = items || [];
    return {
        responsible: list.filter(isResponsible),
        assigned: list.filter((item) => !isResponsible(item)),
    };
};

export const groupProjectsByAccount = (projects) => {
    const groups = [];
    const byAccount = new Map();
    for (const project of sortProjects(projects)) {
        const name = project.account?.name || "No account";
        let group = byAccount.get(name);
        if (!group) {
            group = { name, account: project.account || null, projects: [] };
            byAccount.set(name, group);
            groups.push(group);
        }
        group.projects.push(project);
    }
    return groups;
};

export const groupTasksUnderProjects = (items) => {
    const { tasks, projects } = splitByType(items);
    const groups = sortProjects(projects).map((project) => ({ project, tasks: [] }));
    const byProjectId = new Map(groups.map((group) => [String(group.project._id), group]));

    const ungrouped = [];
    for (const task of tasks) {
        const group = task.project?._id ? byProjectId.get(String(task.project._id)) : null;
        if (group) group.tasks.push(task);
        else ungrouped.push(task);
    }

    for (const group of groups) group.tasks = sortTasksByUrgency(group.tasks);
    return { groups, ungrouped: sortTasksByUrgency(ungrouped) };
};

export const buildStats = (items) => {
    const { tasks, projects } = splitByType(items);
    const assigned = (items || []).filter((item) => !isResponsible(item));
    let overdue = 0;
    let today = 0;
    let rush = 0;
    for (const task of tasks) {
        if (task.rush) rush += 1;
        const tone = dueTone(task.dueDate);
        if (tone === "late") overdue += 1;
        else if (tone === "today") today += 1;
    }
    return {
        total: (items || []).length,
        tasks: tasks.length,
        projects: projects.length,
        accounts: new Set(projects.map((project) => project.account?.name || "No account")).size,
        assigned: assigned.length,
        assignedTasks: assigned.filter(isTask).length,
        assignedProjects: assigned.filter(isProject).length,
        overdue,
        today,
        rush,
    };
};
